import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { errorText, get, setOrganisation } from 'src/common/api';
import { useAuth } from 'src/context/AuthContext';

// Shared state for every page: the organisation (?workspace=), the chosen ad
// account (?account=), the reporting range, the Meta connection snapshot and
// the catalog of goals and options from the backend.
const WorkspaceContext = createContext(null);
const RANGE_KEY = 'autoappy.meta.range';

export const RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7d', label: 'Last 7 days' },
  { value: 'last_14d', label: 'Last 14 days' },
  { value: 'last_30d', label: 'Last 30 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'last_90d', label: 'Last 90 days' },
];

const readRange = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(RANGE_KEY));
    if (stored?.preset) return stored;
  } catch { /* ignore */ }
  return { preset: 'last_7d' };
};

export function WorkspaceProvider({ children }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const organisations = useMemo(() => user?.workspaces || [], [user]);
  const requested = params.get('workspace');
  const organisationId = useMemo(() => {
    if (requested && organisations.some((o) => o.id === requested)) return requested;
    const current = user?.current_workspace?.id;
    if (current && organisations.some((o) => o.id === current)) return current;
    return organisations[0]?.id || '';
  }, [requested, organisations, user]);
  setOrganisation(organisationId);

  const [boot, setBoot] = useState(null);
  const [bootError, setBootError] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [range, setRangeState] = useState(readRange);
  const loadedFor = useRef('');

  const refresh = useCallback(async () => {
    if (!organisationId) return null;
    try {
      const response = await get('/api/bootstrap');
      setBoot(response.data);
      setBootError(null);
      return response.data;
    } catch (error) {
      setBootError(errorText(error));
      return null;
    }
  }, [organisationId]);

  useEffect(() => {
    if (!organisationId || loadedFor.current === organisationId) return;
    loadedFor.current = organisationId;
    setBoot(null);
    refresh();
  }, [organisationId, refresh]);

  useEffect(() => {
    if (catalog || !organisationId) return;
    get('/api/catalog').then((response) => setCatalog(response.data)).catch(() => {});
  }, [catalog, organisationId]);

  // Keep ?workspace= in the address so links can be shared.
  useEffect(() => {
    if (organisationId && params.get('workspace') !== organisationId) {
      const next = new URLSearchParams(params);
      next.set('workspace', organisationId);
      setParams(next, { replace: true });
    }
  }, [organisationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const accounts = useMemo(() => (boot?.meta?.accounts || []).filter((a) => a.enabled), [boot]);
  const requestedAccount = params.get('account');
  const account = useMemo(() => accounts.find((a) => a.account_id === requestedAccount)
    || accounts.find((a) => a.is_default) || accounts[0] || null, [accounts, requestedAccount]);

  const chooseAccount = useCallback((id) => {
    const next = new URLSearchParams(params);
    next.set('account', id);
    ['focus', 'campaign_id', 'adset_id'].forEach((key) => next.delete(key));
    setParams(next);
  }, [params, setParams]);

  const chooseOrganisation = useCallback((id) => {
    const next = new URLSearchParams();
    next.set('workspace', id);
    loadedFor.current = '';
    const base = /^\/(drafts|create|automations)\/[^/]+/.test(location.pathname) ? location.pathname.split('/').slice(0, 2).join('/') : location.pathname;
    navigate(`${base}?${next}`);
  }, [location.pathname, navigate]);

  const setRange = useCallback((value) => {
    setRangeState(value);
    try { localStorage.setItem(RANGE_KEY, JSON.stringify(value)); } catch { /* ignore */ }
  }, []);

  const organisation = organisations.find((o) => o.id === organisationId) || null;
  const canManage = ['owner', 'admin'].includes(organisation?.role);
  const pages = boot?.meta?.pages || [];
  const defaultPage = pages.find((p) => p.is_default) || pages[0] || null;

  /** Add the organisation (and account) to an in-app link. */
  const link = useCallback((path, extra = {}) => {
    const [withoutHash, hash = ''] = path.split('#');
    const [base, query = ''] = withoutHash.split('?');
    const next = new URLSearchParams(query);
    if (organisationId) next.set('workspace', organisationId);
    if (account && !next.has('account') && extra.account !== false) next.set('account', account.account_id);
    Object.entries(extra).forEach(([key, value]) => { if (key !== 'account' && value !== undefined && value !== null) next.set(key, value); });
    return `${base}?${next}${hash ? `#${hash}` : ''}`;
  }, [organisationId, account]);

  const value = {
    user, organisations, organisation, organisationId, chooseOrganisation, canManage,
    boot, bootError, refresh, catalog,
    connected: (boot?.meta?.connections || []).length > 0,
    accounts, account, chooseAccount, currency: account?.currency || 'INR',
    pages, defaultPage, pixels: boot?.meta?.pixels || [], settings: boot?.settings || null,
    demo: Boolean(boot?.meta?.demo), configured: Boolean(boot?.meta?.configured),
    range, setRange, link,
  };

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export const useWorkspace = () => useContext(WorkspaceContext);

/** Range query parameters for report endpoints. */
export const rangeParams = (range) => (range?.preset === 'custom' ? { range: 'custom', since: range.since, until: range.until } : { range: range?.preset || 'last_7d' });

/**
 * Load data and keep loading / error state. `key` decides when to reload;
 * `reload()` refetches without clearing what is on screen.
 */
export function useLoad(load, key, { enabled = true } = {}) {
  const [state, setState] = useState({ data: null, error: null, loading: Boolean(enabled) });
  const latest = useRef(0);
  const run = useCallback(async (quiet = false) => {
    if (!enabled) return;
    const ticket = ++latest.current;
    if (!quiet) setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await load();
      if (ticket === latest.current) setState({ data, error: null, loading: false });
    } catch (error) {
      if (ticket === latest.current) setState((s) => ({ data: quiet ? s.data : null, error: errorText(error), loading: false }));
    }
  }, [enabled, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [run]);

  return { ...state, reload: () => run(true) };
}
